import { memo, type MutableRefObject } from 'react';

import { datapointIndicatorLayout } from './XYChartExplore.sty.js';
import { Canvas } from '../../../../core/components/canvas/Canvas.js';
import { CanvasContext } from '../../../../core/components/canvas/CanvasContext.js';
import type { CanvasShape } from '../../../../core/components/canvas/types/canvas-shape.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { OVERLAY_PADDING } from '../../../../core/components/data-point/constants.js';
import { RectPointCanvas } from '../../../../core/components/data-point/RectPointCanvas.js';
import type { RectLimits } from '../../../../core/components/data-point/utils/get-rect-perimeter-lines.js';
import { getValidColor } from '../../../../core/utils/getValidColor.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { useRectData } from '../../../hooks/useRectData.js';
import { useVisibleAxesConfig } from '../../../hooks/useVisibleAxesConfig.js';
import { useXYChartColorScales } from '../../../hooks/useXYChartColorScales.js';
import { useXYChartScales } from '../../../hooks/useXYChartScales.js';
import { useContentState } from '../../../store/selectors.js';
import { RECT_ID, RECT_VARIANT } from '../../../types/xy-chart-internals.js';
import { getCurrentScale } from '../../../utils/get-scale.js';
import {
  colorAccessor,
  heightAccessor,
  widthAccessor,
  xAccessor,
  yAccessor,
} from '../../displays/rect-accessors.js';

/**
 * Component for rendering the rect datapoints on the Y axis based on an X coordinate.
 */
export const XYChartRectPoint = memo(() => {
  const { series, type, datapoint } = useContentState();
  const { width, height } = useGridPlotSize();

  const { seriesToMetadata: rectSeries } = useRectData();
  const axisIdToScales = useXYChartScales();
  const colorScalesMap = useXYChartColorScales();

  const { left, top, right, bottom } = useVisibleAxesConfig();

  if (!series || !datapoint || type !== RECT_VARIANT) {
    return;
  }

  const { xAxis, yAxis } = safeGet(rectSeries, series);
  const xScale = getCurrentScale(axisIdToScales, xAxis.id);
  const yScale = getCurrentScale(axisIdToScales, yAxis.id);
  const colorScale = safeGet(colorScalesMap, series.id);
  const color = getValidColor(colorAccessor(colorScale)(datapoint));

  const rectPoint = {
    color: { Default: color },
    id: RECT_ID,
    x: xAccessor(xScale)(datapoint) + OVERLAY_PADDING,
    y: yAccessor(yScale)(datapoint) + OVERLAY_PADDING,
    width: widthAccessor(xScale)(datapoint),
    height: heightAccessor(yScale)(datapoint),
  };

  const conditions: RectLimits = {
    drawableTop: !!top,
    drawableRight: !!right,
    drawableBottom: !!bottom,
    drawableLeft: !!left,
  };

  return (
    <div className={datapointIndicatorLayout}>
      <CanvasContext.Provider
        value={{
          shapeRefs: new Map<string, MutableRefObject<CanvasShape>>(),
        }}
      >
        <Canvas
          width={width + OVERLAY_PADDING * 2}
          height={height + OVERLAY_PADDING * 2}
        >
          <RectPointCanvas
            point={rectPoint}
            conditions={conditions}
            width={width}
            height={height}
          />
        </Canvas>
      </CanvasContext.Provider>
    </div>
  );
});

XYChartRectPoint['displayName'] = 'XYChartRectPoint';
